import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

const CameraScanner = ({ onDetected, onClose }) => {
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const animRef = React.useRef(null);
  const readerRef = React.useRef(null);
  const [status, setStatus] = React.useState('Starting camera…');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let stopped = false;

    const stop = () => {
      stopped = true;
      cancelAnimationFrame(animRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      try { if (readerRef.current) readerRef.current.reset(); } catch(e) {}
    };

    const start = async () => {
      // Step 1: Get camera stream — always request rear camera
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
      } catch(e) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        } catch(e2) {
          setError('❌ Camera permission denied. Please allow camera access in your browser settings and try again.');
          return;
        }
      }

      if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play().catch(() => {});
      setStatus('📷 Point camera at barcode…');

      // Step 2a: Try native BarcodeDetector API (Chrome Android, fast & reliable)
      if ('BarcodeDetector' in window) {
        setStatus('📷 Scanning… (hold steady)');
        const detector = new window.BarcodeDetector({ formats: ['ean_13','ean_8','code_128','code_39','upc_a','upc_e','qr_code','itf','data_matrix','aztec','pdf417'] });
        const scan = async () => {
          if (stopped) return;
          try {
            const barcodes = await detector.detect(video);
            if (barcodes.length > 0) {
              stop();
              onDetected(barcodes[0].rawValue);
              return;
            }
          } catch(e) {}
          animRef.current = requestAnimationFrame(scan);
        };
        animRef.current = requestAnimationFrame(scan);
        return;
      }

      // Step 2b: Fallback — ZXing library
      if (window.ZXing) {
        setStatus('📷 Scanning… (hold steady)');
        const ZXing = window.ZXing;
        const hints = new Map();
        hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
          ZXing.BarcodeFormat.EAN_13, ZXing.BarcodeFormat.EAN_8,
          ZXing.BarcodeFormat.CODE_128, ZXing.BarcodeFormat.CODE_39,
          ZXing.BarcodeFormat.UPC_A, ZXing.BarcodeFormat.UPC_E,
          ZXing.BarcodeFormat.QR_CODE, ZXing.BarcodeFormat.ITF,
          ZXing.BarcodeFormat.DATA_MATRIX,
        ]);
        hints.set(ZXing.DecodeHintType.TRY_HARDER, true);
        const reader = new ZXing.BrowserMultiFormatReader(hints);
        readerRef.current = reader;

        // Use canvas-based scanning for reliability
        const canvas = canvasRef.current || document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const scanFrame = () => {
          if (stopped) return;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            try {
              const luminanceSource = new ZXing.HTMLCanvasElementLuminanceSource(canvas);
              const binaryBitmap = new ZXing.BinaryBitmap(new ZXing.HybridBinarizer(luminanceSource));
              const result = new ZXing.MultiFormatReader(hints).decode(binaryBitmap);
              if (result) { stop(); onDetected(result.getText()); return; }
            } catch(e) { /* no barcode this frame */ }
          }
          animRef.current = requestAnimationFrame(scanFrame);
        };
        animRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      setError('❌ Barcode scanning library not available. Please refresh the page.');
    };

    start();
    return stop;
  }, []);

  return (
    <div className="fixed inset-0 bg-black z-[500] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-[#1a1a1a]">
        <span className="text-white font-bold flex items-center gap-2 text-sm"><ScanBarcode size={18}/> Scan Barcode</span>
        <button onClick={onClose} className="text-white p-2 rounded-full hover:bg-white/10"><X size={22}/></button>
      </div>
      <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center">
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
        <canvas ref={canvasRef} className="hidden" />
        {/* Aim overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-64 h-40">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#008060] rounded-tl-sm"/>
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#008060] rounded-tr-sm"/>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#008060] rounded-bl-sm"/>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#008060] rounded-br-sm"/>
            <div className="absolute inset-x-0 top-1/2 h-0.5 bg-[#008060]/70 animate-pulse"/>
          </div>
        </div>
        {!error && (
          <div className="absolute bottom-8 left-0 right-0 flex justify-center">
            <span className="bg-black/70 text-white text-sm px-5 py-2.5 rounded-full font-medium">{status}</span>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/85 p-6">
            <div className="text-center">
              <p className="text-white font-semibold mb-2 text-base">{error}</p>
              <button onClick={onClose} className="mt-4 bg-white text-black px-6 py-2.5 rounded-lg font-semibold text-sm">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraScanner;
